/**
 * Trigger DAG and LP rebuild for a trip.
 * Call only after trip creation or when the user clicks "Recalculate Plan".
 */

import { db } from '../instant';
import { inferRouteStructure } from './routeInference';
import { buildTripDagFromRouteStructure } from './tripDag';
import { rebuildTripDag } from './tripDagDb';
import { rebuildTripLpSolution, buildRequirementsFromPackingLists, type DagNodeRow, type DagEdgeRow } from './tripLpDb';
import {
    computePlanInputsFingerprintSync,
    formatPlanInputsFingerprintStored,
    relevantIncompatibilityPairsForTrip,
} from './planInputsFingerprint';
import {
    bannedEdgeKeysForCargoItem,
    buildTrailersPerEdge,
    buildIncompatibilityKeySet,
    describeInfeasibilityForCargoItem,
    incompatibilityPairsFromRows,
} from './trailerCargoBan';
import type { LpSolveFailureDetail } from './tripLpSolver';

type EventWithPacking = {
    id: string;
    name?: string;
    /** Timestamp or ISO date string (InstantDB may return string). */
    startDate?: number | string;
    endDate?: number | string;
    packingListItems?: Array<{
        itemType?: { id: string; name?: string; category?: { name: string } | null; consumable?: boolean } | null;
        quantity?: number;
    }>;
};

type TripForRebuild = {
    id: string;
    events?: EventWithPacking[];
    sourceStorageLocation?: { id: string; name?: string } | null;
    destStorageLocation?: { id: string; name?: string } | null;
    tripDagNodes?: Array<{ id: string }>;
    tripDagEdges?: Array<{ id: string }>;
};

export type TrailerCargoIncompatibilityRow = {
    id?: string;
    trailerItemType?: { id: string } | null;
    cargoItemType?: { id: string } | null;
};

export type RebuildProgressStep = 'loading' | 'route' | 'dag' | 'requirements' | 'solve' | 'lpClear' | 'lpWrite';

export type RebuildResult = {
    dagNodes: DagNodeRow[];
    dagEdges: DagEdgeRow[];
    lpSolution: Record<
        string,
        { A: Array<{ nodeIndex: number; quantity: number }>; T: Array<{ fromNodeIndex: number; toNodeIndex: number; value: number }> }
    >;
};

/** Run DAG + LP rebuild for one trip (caller must have loaded trip with events, packing lists, source/dest, existing DAG/LP rows). */
async function runRebuildForTrip(
    trip: TripForRebuild,
    onProgress?: (step: RebuildProgressStep) => void,
    trailerCargoIncompatibilities: TrailerCargoIncompatibilityRow[] = []
): Promise<RebuildResult> {
    const tripEvents = trip.events ?? [];
    const tripEventIds = tripEvents.map(e => e.id);
    onProgress?.('route');
    const eventsMap = new Map(
        tripEvents.map(ev => [
            ev.id,
            {
                id: ev.id,
                name: ev.name,
                startDate: ev.startDate != null ? new Date(ev.startDate) : null,
                endDate: ev.endDate != null ? new Date(ev.endDate) : null,
            },
        ])
    );

    const packingListsForInference = new Map(
        (trip.events ?? []).map(ev => {
            const items = (ev.packingListItems ?? [])
                .filter(i => i.itemType?.id)
                .map(i => ({
                    itemType: i.itemType
                        ? {
                              id: i.itemType.id,
                              category: i.itemType.category ? { name: i.itemType.category.name } : null,
                          }
                        : null,
                }));
            return [ev.id, items];
        })
    );

    const sourceId = trip.sourceStorageLocation?.id ?? null;
    const destId = trip.destStorageLocation?.id ?? null;
    if (!sourceId || !destId) throw new Error('Set source and destination storage locations first');

    const routeStructure = inferRouteStructure([], tripEventIds, eventsMap, packingListsForInference, [sourceId], [destId]);

    const dag = buildTripDagFromRouteStructure(routeStructure, sourceId, destId);

    onProgress?.('dag');
    await rebuildTripDag(trip.id, trip.tripDagNodes ?? [], trip.tripDagEdges ?? [], routeStructure, sourceId, destId);

    const dagNodes: DagNodeRow[] = dag.nodes.map(n => ({
        index: n.index,
        kind: n.kind,
        eventId: n.eventId ?? undefined,
        storageLocationId: n.storageLocationId ?? undefined,
    }));
    const dagEdges: DagEdgeRow[] = dag.edges.map(e => ({
        fromNodeIndex: e.fromNodeIndex,
        toNodeIndex: e.toNodeIndex,
    }));

    const packingListsByEvent = new Map<string, Array<{ itemTypeId: string; quantity: number }>>();
    const consumableByItemType: Record<string, boolean> = {};
    const itemNameMap = new Map<string, string>();
    for (const ev of trip.events ?? []) {
        const items: Array<{ itemTypeId: string; quantity: number }> = [];
        for (const i of ev.packingListItems ?? []) {
            const it = i.itemType;
            if (!it?.id) continue;
            consumableByItemType[it.id] = it.consumable ?? false;
            const name = it.name;
            if (name) itemNameMap.set(it.id, name);
            items.push({ itemTypeId: it.id, quantity: i.quantity ?? 0 });
        }
        if (items.length > 0) packingListsByEvent.set(ev.id, items);
    }
    const getItemDisplayName = (id: string) => itemNameMap.get(id) ?? id;

    onProgress?.('requirements');
    const requirements = buildRequirementsFromPackingLists(dagNodes, packingListsByEvent);
    const lpOnProgress = onProgress ? (step: 'solve' | 'lpClear' | 'lpWrite') => onProgress(step) : undefined;

    const allPairs = incompatibilityPairsFromRows(trailerCargoIncompatibilities);
    const incompatibilityKeys = buildIncompatibilityKeySet(allPairs);
    const trailersPerEdge = buildTrailersPerEdge(routeStructure, dagNodes, sourceId, destId);
    const bannedEdgesByItemType: Record<string, Set<string>> = {};
    for (const itemTypeId of Object.keys(requirements)) {
        bannedEdgesByItemType[itemTypeId] = bannedEdgeKeysForCargoItem(itemTypeId, dagEdges, trailersPerEdge, incompatibilityKeys);
    }

    const lpResult = await rebuildTripLpSolution(
        trip.id,
        dagNodes,
        dagEdges,
        requirements,
        consumableByItemType,
        lpOnProgress,
        bannedEdgesByItemType
    );
    if (!lpResult.ok) {
        let msg = lpResult.message;
        const fail = lpResult.failure;
        if (fail) {
            const R = requirements[fail.itemTypeId];
            if (R && R.length === dagNodes.length) {
                const getNodeLabel = (nodeIndex: number) => {
                    const n = dagNodes[nodeIndex];
                    if (!n) return '';
                    if (n.kind === 'source') return trip.sourceStorageLocation?.name ?? 'Source';
                    if (n.kind === 'dest') return trip.destStorageLocation?.name ?? 'Destination';
                    if (n.kind === 'event' && n.eventId) return eventsMap.get(n.eventId)?.name ?? n.eventId;
                    return '';
                };
                msg = describeInfeasibilityForCargoItem(
                    fail.itemTypeId,
                    dagNodes,
                    dagEdges,
                    R,
                    routeStructure,
                    allPairs,
                    sourceId,
                    destId,
                    eid => eventsMap.get(eid)?.name ?? eid,
                    {
                        getCargoName: getItemDisplayName,
                        getTrailerName: getItemDisplayName,
                        getNodeLabel,
                    }
                );
            }
        }
        throw Object.assign(new Error(msg), { failure: fail });
    }

    const eventsById = new Map(
        tripEvents.map(ev => [
            ev.id,
            {
                id: ev.id,
                startDate: ev.startDate ?? null,
                endDate: ev.endDate ?? null,
                packingList: (packingListsByEvent.get(ev.id) ?? []).map(({ itemTypeId, quantity }) => ({
                    itemTypeId,
                    quantity,
                    consumable: consumableByItemType[itemTypeId] ?? false,
                })),
            },
        ])
    );
    const fingerprintPairs = relevantIncompatibilityPairsForTrip(trip, allPairs);
    const fingerprintRaw = computePlanInputsFingerprintSync(tripEventIds, sourceId, destId, eventsById, fingerprintPairs);
    const fingerprint = formatPlanInputsFingerprintStored(fingerprintRaw);
    const now = new Date();
    await db.transact([db.tx.trips[trip.id].update({ planRecalculatedAt: now, planInputsFingerprint: fingerprint })]);

    return {
        dagNodes,
        dagEdges,
        lpSolution: lpResult.solutions ?? {},
    };
}

/**
 * Rebuild DAG and LP solution for a single trip by id.
 * Use after trip creation or when the user clicks "Recalculate Plan".
 * Returns { ok: true, ...result } with fresh DAG and LP so the UI can update immediately, or { ok: false, message }.
 * Optional onProgress is called with the current step for loading UI.
 * Pass tripData when you already have the trip (e.g. from useQuery) so we skip queryOnce and it works offline.
 */
export async function rebuildDagAndLpForTrip(
    tripId: string,
    onProgress?: (step: RebuildProgressStep) => void,
    tripData?: TripForRebuild | null,
    trailerCargoIncompatibilities?: TrailerCargoIncompatibilityRow[] | null
): Promise<
    | { ok: true; dagNodes: RebuildResult['dagNodes']; dagEdges: RebuildResult['dagEdges']; lpSolution: RebuildResult['lpSolution'] }
    | { ok: false; message: string; failure?: LpSolveFailureDetail }
> {
    try {
        onProgress?.('loading');
        let trip: TripForRebuild | undefined;
        let incRows: TrailerCargoIncompatibilityRow[] = trailerCargoIncompatibilities ?? [];
        if (tripData != null) {
            trip = tripData;
        } else {
            const result = await db.queryOnce({
                trips: {
                    $: { where: { id: tripId } },
                    events: {
                        packingListItems: {
                            itemType: {
                                category: {},
                            },
                        },
                    },
                    sourceStorageLocation: {},
                    destStorageLocation: {},
                    tripDagNodes: {},
                    tripDagEdges: {},
                },
                trailerCargoIncompatibilities: {
                    trailerItemType: {},
                    cargoItemType: {},
                },
            });
            const trips = result.data?.trips ?? [];
            trip = (trips as TripForRebuild[])[0];
            if (trailerCargoIncompatibilities == null) {
                incRows = (result.data?.trailerCargoIncompatibilities ?? []) as TrailerCargoIncompatibilityRow[];
            }
        }
        if (!trip) return { ok: false, message: 'Trip not found' };

        const sourceId = trip.sourceStorageLocation?.id ?? null;
        const destId = trip.destStorageLocation?.id ?? null;
        if (!sourceId || !destId) {
            return { ok: false, message: 'Set source and destination storage locations first' };
        }

        const rebuildResult = await runRebuildForTrip(trip, onProgress, incRows);
        return { ok: true, ...rebuildResult };
    } catch (e) {
        const message = e instanceof Error ? e.message : 'Recalculate failed';
        const failure = e && typeof e === 'object' && 'failure' in e ? (e as { failure?: LpSolveFailureDetail }).failure : undefined;
        return { ok: false, message, failure };
    }
}

/**
 * Rebuild DAG and LP solution for every trip that includes the given event.
 * No-op if the event is not in any trip. Runs asynchronously; catch errors in caller.
 */
export async function rebuildDagAndLpForTripsContainingEvent(eventId: string): Promise<void> {
    const result = await db.queryOnce({
        events: {
            $: { where: { id: eventId } },
            trips: {
                events: {
                    packingListItems: {
                        itemType: {
                            category: {},
                        },
                    },
                },
                sourceStorageLocation: {},
                destStorageLocation: {},
                tripDagNodes: {},
                tripDagEdges: {},
            },
        },
        trailerCargoIncompatibilities: {
            trailerItemType: {},
            cargoItemType: {},
        },
    });

    const eventsData = result.data?.events;
    const eventRow = eventsData?.[0];
    const trips = (eventRow?.trips ?? []) as TripForRebuild[];
    if (trips.length === 0) return;

    const incRows = (result.data?.trailerCargoIncompatibilities ?? []) as TrailerCargoIncompatibilityRow[];
    for (const trip of trips) {
        await runRebuildForTrip(trip, undefined, incRows);
    }
}
